/**
 * Centered Split Navigation
 * 
 * Navigation with centered logo and menu items split on both sides.
 * Elegant design for brand-focused websites.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Calendar, Users, Heart, HelpCircle } from "lucide-react";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { useHelpDialog } from "@/hooks/useHelpDialog";
import HelpDialog from "@/components/help/HelpDialog";

const NavigationCenteredSplit = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { currentLayout } = useLayout();
  const { features, navigation } = useSiteSettings();
  const config = useSiteConfig();
  const { navigationItems: navItems } = useNavigationItems();
  const helpDialog = useHelpDialog();

  const isActive = (path: string) => location.pathname === path;
  const navConfig = currentLayout.navigation;

  // Split nav items into left and right groups
  const midpoint = Math.ceil(navItems.length / 2);
  const leftItems = navItems.slice(0, midpoint);
  const rightItems = navItems.slice(midpoint);

  return (
    <nav 
      className={cn(
        "bg-background/95 backdrop-blur-sm border-b border-border z-50 shadow-warm",
        navConfig.sticky && "sticky top-0",
        navConfig.transparent && "bg-transparent border-transparent shadow-none"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Left Navigation Items */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 justify-end pr-8">
            {leftItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={cn(
                  "px-3 py-2 text-sm font-medium transition-colors",
                  isActive(item.path)
                    ? "text-heritage border-b-2 border-heritage"
                    : "text-foreground hover:text-heritage"
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Centered Logo */}
          <Link to="/" className="flex flex-col items-center space-y-1 px-6">
            {config.logoUrl && config.logoUrl.trim() !== '' && (
              <img
                src={config.logoUrl}
                alt={config.organizationName}
                className="h-10 w-auto object-contain mb-1"
                onError={(e) => {
                  console.error('[NavigationCenteredSplit] Failed to load logo:', config.logoUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span className="text-lg font-bold text-foreground whitespace-nowrap">
              {config.organizationName}
            </span>
          </Link>

          {/* Right Navigation Items */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 pl-8">
            {rightItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={cn(
                  "px-3 py-2 text-sm font-medium transition-colors",
                  isActive(item.path)
                    ? "text-heritage border-b-2 border-heritage"
                    : "text-foreground hover:text-heritage"
                )}
              >
                {item.name}
              </Link>
            ))}
            
            {/* CTA Buttons */}
            <div className="flex items-center space-x-2 ml-4">
              {features.membership.enabled && (
                <Button variant="heritage" size="sm" asChild>
                  <Link to="/membership">
                    <Users className="w-4 h-4" />
                  </Link>
                </Button>
              )}
              {features.donation.enabled && (
                <Button variant="donate" size="sm" asChild>
                  <Link to="/donate">
                    <Heart className="w-4 h-4" />
                  </Link>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={helpDialog.open}
                className="text-foreground hover:text-heritage"
                aria-label="Help"
              >
                <HelpCircle className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden absolute right-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-foreground hover:text-heritage"
              aria-label="Toggle menu"
              tabIndex={0}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background border-t border-border">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "block px-3 py-2 text-base font-medium transition-colors text-center",
                    isActive(item.path)
                      ? "text-heritage bg-secondary"
                      : "text-foreground hover:text-heritage hover:bg-secondary"
                  )}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile CTA Buttons */}
              <div className="px-3 py-4 space-y-2">
                {features.events.enabled && navigation.showEventsButton && (
                  <Button variant="culture" className="w-full" asChild>
                    <Link to="/events" onClick={() => setIsOpen(false)}>
                      <Calendar className="w-4 h-4 mr-2" />
                      {features.events.buttonText}
                    </Link>
                  </Button>
                )}
                {features.membership.enabled && (
                  <Button variant="heritage" className="w-full" asChild>
                    <Link to="/membership" onClick={() => setIsOpen(false)}>
                      <Users className="w-4 h-4 mr-2" />
                      {features.membership.buttonText}
                    </Link>
                  </Button>
                )}
                {features.donation.enabled && (
                  <Button variant="donate" className="w-full" asChild>
                    <Link to="/donate" onClick={() => setIsOpen(false)}>
                      <Heart className="w-4 h-4 mr-2" />
                      {features.donation.buttonText}
                    </Link>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    helpDialog.open();
                    setIsOpen(false);
                  }}
                  aria-label="Help"
                >
                  <HelpCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <HelpDialog open={helpDialog.isOpen} onOpenChange={helpDialog.setOpen} />
    </nav>
  );
};

export default NavigationCenteredSplit;
